import React, { useState, useEffect } from 'react';
import API from '../../lib/api';
import { Zap, Trophy, Brain, Target, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Reusable component that shows the saved engine prediction for today's match.
 * Each dashboard page passes its engine_key to display the relevant prediction.
 * 
 * Props:
 *   engineKey: string — e.g., "elo_rating", "monte_carlo", "external_intelligence", "god_mode"
 *   matchId: string (optional) — specific match to show. If empty, shows nearest upcoming.
 */
export default function EnginePredictionBanner({ engineKey, matchId }) {
    const [data, setData] = useState(null);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        if (!matchId) return;
        API.get(`/engine-predictions/${matchId}`).then(res => {
            const pred = res.data?.predictions?.find(p => p.engine_key === engineKey);
            if (pred) setData(pred);
        }).catch(() => {});
    }, [matchId, engineKey]);

    if (!data) return null;

    const isGodMode = engineKey === 'god_mode';
    const borderColor = isGodMode ? 'border-amber-500/20' : 'border-cyan-500/10';
    const bgColor = isGodMode ? 'bg-amber-500/[0.03]' : 'bg-cyan-500/[0.02]';

    return (
        <div className={`glass-card p-4 border ${borderColor} ${bgColor} mb-4`} data-testid={`engine-prediction-${engineKey}`}>
            <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
                <div className="flex items-center gap-2">
                    {isGodMode ? <Brain className="w-4 h-4 text-amber-400" /> : <Target className="w-4 h-4 text-cyan-400" />}
                    <div>
                        <div className="text-[10px] text-white/30 tech-mono">
                            {isGodMode ? 'GOD MODE PREDICTION' : `${data.engine_name?.toUpperCase()} PREDICTION`}
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-white">
                                {data.match?.home?.short_name || '?'} vs {data.match?.away?.short_name || '?'}
                            </span>
                            <span className="text-xs text-cyan-400 font-bold">{data.predicted_winner || '?'}</span>
                            <span className="text-[10px] text-white/20">({data.team_a_pct}% - {data.team_b_pct}%)</span>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {data.confidence && (
                        <span className="text-[10px] text-amber-400 tech-mono">{(data.confidence * 100).toFixed(0)}% conf.</span>
                    )}
                    <span className="text-[10px] text-green-400/50 tech-mono px-1.5 py-0.5 rounded bg-green-500/5">POST-TOSS</span>
                    {expanded ? <ChevronUp className="w-3 h-3 text-white/20" /> : <ChevronDown className="w-3 h-3 text-white/20" />}
                </div>
            </div>

            {expanded && (
                <div className="mt-3 pt-3 border-t border-white/5">
                    {/* Probability bar */}
                    <div className="flex items-center gap-3 mb-2">
                        <span className="text-xs font-bold min-w-[35px]" style={{ color: data.match?.home?.color }}>
                            {data.team_a_pct}%
                        </span>
                        <div className="flex-1 h-3 bg-white/5 rounded-full overflow-hidden flex">
                            <div className="h-full rounded-l-full" style={{ width: `${data.team_a_pct}%`, backgroundColor: data.match?.home?.color }} />
                            <div className="h-full rounded-r-full flex-1" style={{ backgroundColor: data.match?.away?.color }} />
                        </div>
                        <span className="text-xs font-bold min-w-[35px] text-right" style={{ color: data.match?.away?.color }}>
                            {data.team_b_pct}%
                        </span>
                    </div>

                    {/* Toss info */}
                    {data.match?.toss_winner && (
                        <div className="text-[10px] text-white/20">
                            Toss: <span className="text-white/40">{data.match.toss_winner}</span> won, chose to <span className="text-white/40">{data.match.toss_decision}</span>
                        </div>
                    )}

                    {/* God Mode extras */}
                    {isGodMode && data.verdict_statement && (
                        <div className="mt-2 text-xs text-white/50 italic">"{data.verdict_statement}"</div>
                    )}
                    {isGodMode && data.key_factors?.length > 0 && (
                        <div className="mt-2 space-y-1">
                            {data.key_factors.slice(0, 3).map((f, i) => (
                                <div key={i} className="flex items-start gap-1.5 text-[10px] text-white/30">
                                    <Zap className="w-2.5 h-2.5 text-amber-400 mt-0.5 shrink-0" />
                                    <span>{f}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Details */}
                    {data.details && !isGodMode && (
                        <div className="mt-2 text-[9px] text-white/15 tech-mono">
                            {Object.entries(data.details).filter(([k, v]) => typeof v !== 'object').slice(0, 4).map(([k, v]) => (
                                <span key={k} className="mr-3">{k}: {typeof v === 'number' ? v.toFixed ? v.toFixed(1) : v : String(v)}</span>
                            ))}
                        </div>
                    )}

                    <div className="text-[9px] text-white/10 mt-2 tech-mono">
                        Generated: {data.triggered_at ? new Date(data.triggered_at).toLocaleString() : 'N/A'}
                    </div>
                </div>
            )}
        </div>
    );
}
